import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { parseISO } from "date-fns";
import { format, zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../../../../components/Form";
import { AppSwitch } from "../../../../../../../../../../../components/Generic/AppSwitch";
import Modal from "../../../../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../../../hooks/useYupValidation";
import { MemberAvailabilityType } from "../../../../../../../../../../../models/member";
import {
  MemberAvailability,
  MemberAvailabilityAllDayAbsence,
  MemberAvailabilityPartialAbsence,
} from "../../../../../../../../../../../models/members/member.availability";
import { useActions } from "../../../../hooks/actions";
import useStyles, { AlldayLabel, AlldaySection, ContentBox, ModalSection } from "./styles";

export type ManageAvailabilityHandles = {
  open: (root?: MemberAvailability) => void;
  close: () => void;
};

type Props = {
  isLoading: boolean;
  onAddNewAvailability?: (
    data: MemberAvailabilityAllDayAbsence | MemberAvailabilityPartialAbsence
  ) => void;
};

const ManageAvailabilityComponent: ForwardRefRenderFunction<ManageAvailabilityHandles, Props> = (
  { onAddNewAvailability, isLoading },
  ref
) => {
  const classes = useStyles();

  const { currentUser, userTimezone } = useAppState();
  const { t: translate } = useTranslation();

  const { member } = useActions();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { getErrorByField } = useYupValidation(errors);

  function isEndDateAfterThenStartDate(startDateString?: string, endDateString?: string) {
    if (startDateString && endDateString) {
      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);

      return endDate >= startDate;
    }

    return false;
  }

  const schema = yup.object().shape({
    type: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    allDayAbsence: yup.boolean(),
    from: yup.string().when("allDayAbsence", {
      is: true,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired"))
        .test("isFromDateLaterThenToDate", translate("pleaseSelectAnEarlierDate"), (startDate) =>
          isEndDateAfterThenStartDate(startDate, endDate)
        ),
      otherwise: yup.string().nullable(),
    }),
    to: yup.string().when("allDayAbsence", {
      is: true,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired"))
        .test("isToDateLaterThenFromDate", translate("pleaseSelectAnLaterDate"), (endDate) =>
          isEndDateAfterThenStartDate(startDate, endDate)
        ),
      otherwise: yup.string().nullable(),
    }),
    date: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    startTime: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    endTime: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
  });

  const [isOpened, setIsOpened] = useState(false);
  const [, setRootData] = useState<MemberAvailability | null>(null);

  const [type, setType] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const toggleIsAllDay = (value: boolean) => {
    setIsAllDay(value);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
  };

  const onResetFields = useCallback(() => {
    setType("");
    setIsAllDay(false);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
  }, []);

  const onValidate = () => {
    try {
      schema.validateSync(
        {
          type,
          allDayAbsence: isAllDay,
          date: startDate,
          from: startDate,
          to: endDate,
          startTime,
          endTime,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }

      return false;
    }
  };

  const onSubmitForm = () => {
    if (onValidate()) {
      let data: MemberAvailabilityAllDayAbsence | MemberAvailabilityPartialAbsence;
      const dayStartTime = "00:00";
      //back end automatically adds 23:59:59 time to the end date when it's an all day absence, so we set it to 00:00 to avoid confusion
      const dayEndTime = "00:00";

      if (isAllDay) {
        data = {
          type,
          allDayAbsence: isAllDay,
          from: joinDateTimeUTC(startDate, dayStartTime),
          to: joinDateTimeUTC(endDate, dayEndTime),
          memberId: Number(member?.id),
          companyId: currentUser?.companyId,
        } as MemberAvailabilityAllDayAbsence;
      } else {
        data = {
          type,
          allDayAbsence: isAllDay,
          from: joinDateTimeUTC(startDate, startTime),
          to: joinDateTimeUTC(startDate, endTime),
          memberId: Number(member?.id),
          companyId: currentUser?.companyId,
        } as MemberAvailabilityPartialAbsence;
      }

      if (data) onAddNewAvailability?.(data);
    }
  };

  const getDateValue = (date: string) => {
    let currentDate = date;

    if (!date) return null;

    const response = format(
      utcToZonedTime(
        currentDate,
        userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone
      ),
      "yyyy-MM-dd"
    );

    return response;
  };

  const setDateValue = (newDate: string, action: React.Dispatch<React.SetStateAction<string>>) => {
    const currentTimezone = userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dateToAct = zonedTimeToUtc(newDate, currentTimezone);

    if (dateToAct) {
      action(dateToAct.toISOString());
    }
  };

  const joinDateTimeUTC = (date: string, time: string) => {
    const currentTimezone = userTimezone?.name;
    const dateUTC = parseISO(date);
    const dateZonedTime = utcToZonedTime(dateUTC.toISOString(), currentTimezone);

    const [hours, minutes] = time.split(":").map(Number);

    dateZonedTime.setHours(hours, minutes, 0, 0);

    const dateTimeUTC = zonedTimeToUtc(dateZonedTime, currentTimezone);

    return dateTimeUTC.toISOString().replace("T", " ").replace(":00.000Z", "");
  };

  const renderSectionContent = () => {
    if (isAllDay) {
      return (
        <Grid item xs={12} container direction="row" spacing={4}>
          <Grid item xs={6}>
            <Input
              name="startDate"
              required
              label={translate("from")}
              type="date"
              value={startDate ? getDateValue(startDate) : ""}
              onChange={(event) => setDateValue(event.target.value, setStartDate)}
              error={!!getErrorByField("from")}
              errorMessage={getErrorByField("from")}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              name="endDate"
              required
              label={translate("to")}
              type="date"
              value={endDate ? getDateValue(endDate) : ""}
              onChange={(event) => setDateValue(event.target.value, setEndDate)}
              error={!!getErrorByField("to")}
              errorMessage={getErrorByField("to")}
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} container direction="row" spacing={4}>
        <Grid item xs={12}>
          <Input
            name="startDate"
            required
            label={translate("date")}
            type="date"
            value={startDate ? getDateValue(startDate) : ""}
            onChange={(event) => setDateValue(event.target.value, setStartDate)}
            error={!!getErrorByField("date")}
            errorMessage={getErrorByField("date")}
          />
        </Grid>

        <Grid item xs={6}>
          <Input
            name="starTime"
            required
            label={translate("startTime")}
            type="time"
            value={startTime}
            onChange={(event) => {
              setStartTime(event.target.value);
            }}
            error={!!getErrorByField("startTime")}
            errorMessage={getErrorByField("startTime")}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            name="endTime"
            required
            label={translate("endTime")}
            type="time"
            value={endTime}
            onChange={(event) => {
              setEndTime(event.target.value);
            }}
            error={!!getErrorByField("endTime")}
            errorMessage={getErrorByField("endTime")}
          />
        </Grid>
      </Grid>
    );
  };

  const onOpenModal = useCallback((root?: MemberAvailability) => {
    setRootData(root || null);
    if (root) {
      setIsAllDay(root?.allDayAbsence);
      setType(root?.type);
      setStartDate(root?.dateFrom || "");
      setEndDate(root?.dateTo || "");
      setStartTime(format(new Date(root?.dateFrom || ""), "HH:mm") || "");
      setEndTime(format(new Date(root?.dateTo || ""), "HH:mm") || "");
    }

    setIsOpened(true);
  }, []);

  const onCloseModal = useCallback(() => {
    onResetFields();
    setRootData(null);
    setIsOpened(false);
    setErrors([]);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <Modal
      open={isOpened}
      handleClose={onCloseModal}
      title={translate("newAbsence")}
      data-cy="absenceModal"
      dataCyTitle="absenceModalTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: onCloseModal,
        dataCySave: "submitChangesButton",
        textSave: translate("create"),
        onSave: onSubmitForm,
        loading: isLoading,
        classesCancel: classes.cancelText,
      }}
      showStatusField={false}
      className={classes.modal}
      modalFooterCustomClass={classes.modalFooter}
    >
      <ModalSection>
        <Grid container direction="row" alignItems="flex-start">
          <Grid item xs={12}>
            <Select
              label={translate("type")}
              required
              onChange={(event: SelectChangeEvent<any>) => setType(event.target.value)}
              value={type}
              placeholder={translate("selectOne")}
              error={!!getErrorByField("type")}
              errorMessage={getErrorByField("type")}
            >
              {Object.values(MemberAvailabilityType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <ContentBox item xs={12} container direction="column">
            <AlldaySection
              item
              xs={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              marginBottom={"8px"}
            >
              <AlldayLabel>{translate("allDayAbsence")}</AlldayLabel>
              <AppSwitch value={isAllDay} onChange={() => toggleIsAllDay(!isAllDay)} />
            </AlldaySection>
            {renderSectionContent()}
          </ContentBox>
        </Grid>
      </ModalSection>
    </Modal>
  );
};

const ManageAvailability = forwardRef(ManageAvailabilityComponent);

export default ManageAvailability;
